// 母音正規化 — プロジェクト全体の正しさの土台。
// ビルド時（コーパス）とクライアント（クエリ）で同一コードを使うことで、両者の
// 母音キーが一致する＝検索が正しく成立することを保証する。
//
// 二段構成:
//   Pass1  toMorae → serialize : カナ読み → アノテート母音列（フラグ非依存・保存対象）
//                                アルファベット = {a,i,u,e,o, N(ん), Q(っ), -(長音)}
//   Pass2  project / toVowelKey : アノテート列 → 最終母音キー（フラグ依存・実行時）

import { applyLongVowelNorm, type Flags } from './toggles'

export type VowelSym = 'a' | 'i' | 'u' | 'e' | 'o'
export type AnnotSym = VowelSym | 'N' | 'Q' | '-'

/** 1モーラ。v=アノテート記号、k=元になったカナ span（ハイライト用）。 */
export interface Mora {
  v: AnnotSym
  k: string
}

// カタカナ基底文字 → 母音（段）。濁音・半濁音・ヴ・ヰ/ヱ/ヲ を含む。
const ROWS: Record<VowelSym, string> = {
  a: 'アカガサザタダナハバパマヤラワヷ',
  i: 'イキギシジチヂニヒビピミリヰ',
  u: 'ウクグスズツヅヌフブプムユルヴ',
  e: 'エケゲセゼテデネヘベペメレヱ',
  o: 'オコゴソゾトドノホボポモヨロヲ',
}
const VOWEL_OF = new Map<string, VowelSym>()
for (const [v, chars] of Object.entries(ROWS)) {
  for (const c of chars) VOWEL_OF.set(c, v as VowelSym)
}

// 小書きカナ（独立モーラを作らず、直前モーラの母音を上書きする）→ その母音。
// 拗音ャュョ、外来音ァィゥェォ、ヮ、ヵヶ を扱う。
const SMALL_VOWEL = new Map<string, VowelSym>([
  ['ャ', 'a'],
  ['ュ', 'u'],
  ['ョ', 'o'],
  ['ァ', 'a'],
  ['ィ', 'i'],
  ['ゥ', 'u'],
  ['ェ', 'e'],
  ['ォ', 'o'],
  ['ヮ', 'a'],
  ['ヵ', 'a'],
  ['ヶ', 'e'],
])

const isVowel = (c: string): c is VowelSym =>
  c === 'a' || c === 'i' || c === 'u' || c === 'e' || c === 'o'

/** ひらがな→カタカナ、半角長音→全角長音に正規化する。 */
function normalizeKana(s: string): string {
  let out = ''
  for (const ch of s) {
    const code = ch.codePointAt(0)
    if (code === undefined) continue
    if (code >= 0x3041 && code <= 0x3096) {
      out += String.fromCodePoint(code + 0x60) // ひらがな → カタカナ
    } else if (ch === 'ｰ') {
      out += 'ー'
    } else {
      out += ch
    }
  }
  return out
}

/**
 * カナ読みをモーラ列に分解する（Pass1）。
 * 未対応文字（記号・空白・ローマ字・数字・「・」など）は無視する。
 */
export function toMorae(kana: string): Mora[] {
  const s = normalizeKana(kana)
  const morae: Mora[] = []
  for (const ch of s) {
    const small = SMALL_VOWEL.get(ch)
    if (small !== undefined) {
      const prev = morae[morae.length - 1]
      if (prev && isVowel(prev.v)) {
        // 直前モーラの母音を上書きし、カナ span を連結（キャ, フィ, ヴォ …）。
        prev.v = small
        prev.k += ch
      } else {
        // 先頭の小書き等の異常系は独立モーラとして扱う（アルファベット内に収める）。
        morae.push({ v: small, k: ch })
      }
      continue
    }
    if (ch === 'ー') {
      morae.push({ v: '-', k: 'ー' })
      continue
    }
    if (ch === 'ッ') {
      morae.push({ v: 'Q', k: 'ッ' })
      continue
    }
    if (ch === 'ン') {
      morae.push({ v: 'N', k: 'ン' })
      continue
    }
    const vowel = VOWEL_OF.get(ch)
    if (vowel !== undefined) {
      morae.push({ v: vowel, k: ch })
    }
    // それ以外は無視
  }
  return morae
}

/** モーラ列をアノテート母音列（1文字/モーラ）へ直列化する。 */
export function serialize(morae: Mora[]): string {
  let out = ''
  for (const m of morae) out += m.v
  return out
}

/**
 * アノテート母音列を最終母音キーへ射影する（Pass2）。
 * map[i] = キー i 文字目が由来するアノテート列（=モーラ配列）の index。ハイライトに使う。
 */
export function project(annotated: string, flags: Flags): { key: string; map: number[] } {
  let out = ''
  const map: number[] = []
  let lastVowel: VowelSym | '' = ''
  for (let i = 0; i < annotated.length; i++) {
    const c = annotated[i] as AnnotSym
    if (isVowel(c)) {
      out += c
      map.push(i)
      lastVowel = c
    } else if (c === '-') {
      if (flags.choon === 'inherit' && lastVowel) {
        out += lastVowel
        map.push(i)
      }
    } else if (c === 'Q') {
      if (flags.sokuon === 'boundary') {
        out += 'Q'
        map.push(i)
      }
    } else if (c === 'N') {
      if (flags.hatsuon === 'include') {
        out += 'N'
        map.push(i)
      }
    }
  }
  // 長音化は ou→oo / ei→ee（長さ不変）なので map はそのまま有効。
  const key = flags.longVowelNorm ? applyLongVowelNorm(out) : out
  return { key, map }
}

/** アノテート母音列 → 最終母音キー（Pass2 のキーのみ）。 */
export function toVowelKey(annotated: string, flags: Flags): string {
  return project(annotated, flags).key
}

/** カナ読み → 最終母音キー（Pass1+Pass2 のショートカット）。 */
export function readingToKey(kana: string, flags: Flags): string {
  return toVowelKey(serialize(toMorae(kana)), flags)
}
